import { useState, useRef } from "react";
import { Box3, Object3D, Vector3 } from "three";

const SIDEBAR_WIDTH = 380;
const DEFAULT_FOV = 75; // градусы

const calculateCameraSetup = (object: Object3D) => {
  const bbox = new Box3().setFromObject(object);
  const size = bbox.getSize(new Vector3());
  const center = bbox.getCenter(new Vector3());

  // Размеры экрана
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const effectiveWidth = viewportWidth - SIDEBAR_WIDTH;
  const effectiveAspect = effectiveWidth / viewportHeight;

  // 3D диагональ модели
  const diagonal = Math.sqrt(size.x ** 2 + size.y ** 2 + size.z ** 2);

  // Расстояние камеры на основе FOV
  const fovRad = DEFAULT_FOV * (Math.PI / 180);
  const visibleHeight = 2 * Math.tan(fovRad / 2);

  // Базовое расстояние с запасом для полного обзора
  let distance = (diagonal / visibleHeight) * 2.0;

  // Учёт узких экранов (с сайдбаром)
  if (effectiveAspect < 1) {
    distance *= 1 / effectiveAspect;
  }

  distance = Math.max(distance, 15);

  // Базовая позиция камеры под углом 45°
  const angle = Math.PI / 4;
  const baseX = center.x + distance * Math.cos(angle);
  const baseY = center.y + distance * 0.8;
  const baseZ = center.z + distance * Math.sin(angle);

  // Вектор "вправо" камеры для смещения
  const cameraDir = new Vector3(center.x - baseX, 0, center.z - baseZ).normalize();
  const worldUp = new Vector3(0, 1, 0);
  const rightVector = new Vector3().crossVectors(worldUp, cameraDir).normalize();

  // Смещение в мировых координатах (SIDEBAR_WIDTH / 2 пикселей)
  const offsetPixels = SIDEBAR_WIDTH / 2;
  const fullAspect = viewportWidth / viewportHeight;
  const visibleWidth = 2 * Math.tan(fovRad / 2) * distance * fullAspect;
  const worldOffset = (offsetPixels / viewportWidth) * visibleWidth;

  // Применяем смещение вправо к позиции и target
  const position: [number, number, number] = [
    baseX + rightVector.x * worldOffset,
    baseY,
    baseZ + rightVector.z * worldOffset,
  ];

  const target: [number, number, number] = [
    center.x + rightVector.x * worldOffset,
    center.y,
    center.z + rightVector.z * worldOffset,
  ];

  return { position, target };
};

export default function useBuilding() {
  const buildingRef = useRef<Object3D | null>(null);
  const [bbox, setBbox] = useState<Box3 | null>(null);
  const [cameraPosition, setCameraPosition] = useState<
    [number, number, number] | undefined
  >(undefined);
  const [cameraTarget, setCameraTarget] = useState<
    [number, number, number] | undefined
  >(undefined);

  const onBuildingLoad = (scene: Object3D) => {
    const { position, target } = calculateCameraSetup(scene);
    setCameraPosition(position);
    setCameraTarget(target);

    buildingRef.current = scene;
    const box = new Box3().setFromObject(scene);
    setBbox(box);
  };

  return { buildingRef, bbox, cameraPosition, cameraTarget, onBuildingLoad } as const;
}
